import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  AuthPanel,
  isProfileStepRequired,
  parseAuthError,
  resolveAuthSuccessPath,
  unlockRegistrationProfileStep,
  useAuth,
} from '@/features/auth'
import { routes } from '@/shared/config'
import { Button } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { Footer } from '@/widgets/footer'
import { Header } from '@/widgets/header'
import bgEpic from '@/shared/assets/registration/bg_epic.png'

const INVALID_LINK_MESSAGE = 'Ссылка недействительна'

const registrationDesktopTokens = designTokens.components.registration.desktop
const registrationMobileTokens = designTokens.components.registration.mobile

const messageStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  fontSize: designTokens.typography.body.base.size,
  lineHeight: designTokens.typography.body.base.lineHeight,
  fontWeight: designTokens.typography.body.base.weight,
  margin: 0,
  textAlign: 'center' as const,
  color: designTokens.colors.text.primary,
}

export function MagicLinkPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { consumeMagicLink } = useAuth()
  const token = searchParams.get('token')?.trim() ?? ''
  const consumedTokenRef = useRef<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(
    token ? null : INVALID_LINK_MESSAGE
  )

  useEffect(() => {
    document.title = 'Вход по ссылке | Epic Growth'
  }, [])

  useEffect(() => {
    if (!token) {
      return
    }

    if (consumedTokenRef.current === token) {
      return
    }

    consumedTokenRef.current = token

    void (async () => {
      try {
        const response = await consumeMagicLink(token)
        const requiresProfileStep = isProfileStepRequired(response)
        if (requiresProfileStep) {
          unlockRegistrationProfileStep()
        }
        navigate(resolveAuthSuccessPath(response), {
          replace: true,
          state: requiresProfileStep ? { allowProfileStepOnce: true } : null,
        })
      } catch (error) {
        setErrorMessage(parseAuthError(error).message)
      }
    })()
  }, [consumeMagicLink, navigate, token])

  return (
    <section
      className="relative w-full"
      style={{
        minHeight: '100vh',
        width: '100%',
        backgroundImage: `url(${bgEpic})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflowX: 'hidden',
      }}
    >
      <div
        className="relative hidden min-h-screen w-full flex-col items-center lg:flex"
        style={{ maxWidth: registrationDesktopTokens.frame.width }}
      >
        <div className="w-full">
          <Header />
        </div>
        <main className="relative flex w-full flex-1 items-start justify-center">
          <div
            className="relative z-10 w-full"
            style={{
              maxWidth: registrationDesktopTokens.panel.width,
              marginTop: designTokens.spacing.authPanelDesktopTop,
            }}
          >
            <AuthPanel
              variant="desktop"
              title="Выполняем вход"
              subtitle={
                errorMessage ? 'Ссылка для входа не сработала.' : 'Подождите, мы авторизуем вас.'
              }
              footer={
                errorMessage ? (
                  <div
                    className="flex w-full flex-col"
                    style={{ gap: designTokens.spacing.authPanelFooterGap }}
                  >
                    <Button variant="primary" onClick={() => navigate(routes.registration)}>
                      Вернуться ко входу
                    </Button>
                  </div>
                ) : null
              }
            >
              <div
                className="flex w-full flex-col items-center"
                style={{ gap: designTokens.spacing.md }}
              >
                <p
                  style={{
                    ...messageStyle,
                    color: errorMessage
                      ? designTokens.colors.text.error
                      : designTokens.colors.text.secondary,
                  }}
                >
                  {errorMessage ?? 'Проверяем ссылку и открываем ваш кабинет...'}
                </p>
              </div>
            </AuthPanel>
          </div>
        </main>
        <div className="w-full" style={{ maxWidth: registrationDesktopTokens.footer.width }}>
          <Footer />
        </div>
      </div>

      <div className="relative flex min-h-screen w-full flex-col items-center lg:hidden">
        <div
          className="relative w-full flex-1"
          style={{
            minHeight: registrationMobileTokens.topAreaMinHeightTall,
            maxWidth: registrationMobileTokens.frame.width,
          }}
        >
          <div
            className="absolute"
            style={{
              left: '50%',
              top: registrationMobileTokens.headerCompact.y,
              width: registrationMobileTokens.headerCompact.width,
              height: registrationMobileTokens.headerCompact.height,
              transform: 'translateX(-50%)',
            }}
          >
            <Header />
          </div>
        </div>
        <div className="mt-auto w-full">
          <AuthPanel
            variant="mobile"
            title="Выполняем вход"
            subtitle={
              errorMessage ? 'Ссылка для входа не сработала.' : 'Подождите, мы авторизуем вас.'
            }
            footer={
              errorMessage ? (
                <div
                  className="flex w-full flex-col"
                  style={{ gap: designTokens.spacing.authPanelFooterGap }}
                >
                  <Button variant="primaryMobile" onClick={() => navigate(routes.registration)}>
                    Вернуться ко входу
                  </Button>
                </div>
              ) : null
            }
          >
            <div
              className="flex w-full flex-col items-center"
              style={{ gap: designTokens.spacing.md }}
            >
              <p
                style={{
                  ...messageStyle,
                  color: errorMessage
                    ? designTokens.colors.text.error
                    : designTokens.colors.text.secondary,
                }}
              >
                {errorMessage ?? 'Проверяем ссылку и открываем ваш кабинет...'}
              </p>
            </div>
          </AuthPanel>
        </div>
      </div>
    </section>
  )
}
